// This will monitor a scroll-slider and update an adjacent scroll-indicator
// This component should contain a scroll slider as well as a scroll indicator
// eg:

// <scroll-slider-with-scroll-indicator>
//   {%- render 'scroll-slider',
//     slider_items: items,
//   -%}
//   {% render 'scroll-indicator', item_count: item_count %}
// </scroll-slider-with-scroll-indicator>
class ScrollSliderWithScrollIndicator extends HTMLElement {
  constructor() {
    super();

    this.selectors = {
      scroller: ".scroll-slider__slider",
      indicatorCursor: ".scroll-indicator__cursor",
    };
  }

  connectedCallback() {
    this.controller = new AbortController();

    this.scroller = this.querySelector(this.selectors.scroller);
    this.indicatorCursor = this.querySelector(this.selectors.indicatorCursor);

    this.scroller.addEventListener("scroll", this.#handleScroll.bind(this), {
      signal: this.controller.signal,
    });
  }

  #handleScroll() {
    const percentageScrolled = (
      this.scroller.scrollLeft /
      (this.scroller.scrollWidth - this.scroller.clientWidth)
    ).toFixed(2);

    if (percentageScrolled !== this.lastPercentageScrolled) {
      this.#updateIndicator(percentageScrolled);
      this.lastPercentageScrolled = percentageScrolled;
    }
  }

  #updateIndicator(percentageScrolled) {
    this.indicatorCursor.style.setProperty("--percentage", percentageScrolled);
    this.indicatorCursor.style.left = `${percentageScrolled * 100}%`;
  }

  /**
   * Rebuilds the slider with new content
   * @param {String} newHTML - The new HTML content for slider items
   * @param {Number} newItemCount - The new item count for the scroll indicator
   */
  rebuildSlider(newHTML, newItemCount) {
    console.log('🔄 Rebuilding slider with', newItemCount, 'items');
    
    // Find the scroll slider component and slider element
    const scrollSliderComponent = this.querySelector('scroll-slider-component, slideshow-component');
    const sliderElement = this.querySelector(this.selectors.scroller);
    
    if (!scrollSliderComponent || !sliderElement) {
      console.error('❌ Slider component or element not found');
      return;
    }

    // Update slider content
    sliderElement.innerHTML = newHTML;
    
    // Update scroll indicator count if provided
    if (newItemCount !== undefined) {
      const scrollIndicator = this.querySelector('.scroll-indicator');
      if (scrollIndicator) {
        scrollIndicator.style.setProperty('--item-count', newItemCount);
      }
    }

    // Reset scroll position to start
    sliderElement.scrollLeft = 0;

    // Trigger slider re-initialization
    // Use multiple requestAnimationFrame calls to ensure DOM is fully updated and rendered
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        // Update scroll-slider component's sliderItems reference
        // This must happen after DOM update but before initScroller
        if (scrollSliderComponent.selectors) {
          scrollSliderComponent.sliderItems = scrollSliderComponent.querySelectorAll(scrollSliderComponent.selectors.slide);
          scrollSliderComponent.slideCount = scrollSliderComponent.sliderItems.length;
          
          if (scrollSliderComponent.slider) {
            scrollSliderComponent.slider.dataset.slideCount = scrollSliderComponent.slideCount;
          }
        }

        // Re-find navigation buttons to ensure references are fresh
        if (scrollSliderComponent.selectors && scrollSliderComponent.dataset) {
          const navigationContainer = scrollSliderComponent.dataset.navigationContainer
            ? scrollSliderComponent.closest(scrollSliderComponent.dataset.navigationContainer)
            : scrollSliderComponent;

          if (navigationContainer) {
            scrollSliderComponent.prevButton = navigationContainer.querySelector(scrollSliderComponent.selectors.prevButton);
            scrollSliderComponent.nextButton = navigationContainer.querySelector(scrollSliderComponent.selectors.nextButton);
            
            // Re-attach button event listeners if buttons exist
            if (scrollSliderComponent.prevButton || scrollSliderComponent.nextButton) {
              // Ensure we have event options
              if (!scrollSliderComponent.eventOptions && scrollSliderComponent.controller) {
                scrollSliderComponent.eventOptions = { signal: scrollSliderComponent.controller.signal };
              }
              
              // Re-attach button click listeners
              // Note: If listeners already exist, they'll be duplicates, but that's okay
              // The AbortController will clean them up when component is destroyed
              if (scrollSliderComponent.prevButton && scrollSliderComponent.onButtonClick) {
                // Remove any existing listeners by cloning (cleaner approach)
                const prevButtonParent = scrollSliderComponent.prevButton.parentNode;
                const prevButtonClone = scrollSliderComponent.prevButton.cloneNode(true);
                prevButtonParent.replaceChild(prevButtonClone, scrollSliderComponent.prevButton);
                scrollSliderComponent.prevButton = prevButtonClone;
                
                // Re-attach click listener
                prevButtonClone.addEventListener(
                  "click",
                  scrollSliderComponent.onButtonClick.bind(scrollSliderComponent),
                  scrollSliderComponent.eventOptions || {}
                );
              }
              
              if (scrollSliderComponent.nextButton && scrollSliderComponent.onButtonClick) {
                // Remove any existing listeners by cloning (cleaner approach)
                const nextButtonParent = scrollSliderComponent.nextButton.parentNode;
                const nextButtonClone = scrollSliderComponent.nextButton.cloneNode(true);
                nextButtonParent.replaceChild(nextButtonClone, scrollSliderComponent.nextButton);
                scrollSliderComponent.nextButton = nextButtonClone;
                
                // Re-attach click listener
                nextButtonClone.addEventListener(
                  "click",
                  scrollSliderComponent.onButtonClick.bind(scrollSliderComponent),
                  scrollSliderComponent.eventOptions || {}
                );
              }
            }
          }
        }

        // Call initScroller which will:
        // - Recalculate dimensions using updated sliderItems
        // - Update button states via handleScrollPositionChange()
        // - Update page dots if needed
        if (scrollSliderComponent.initScroller) {
          scrollSliderComponent.initScroller();
          
          // Force button state update after a brief delay to ensure everything is calculated
          setTimeout(() => {
            if (scrollSliderComponent.handleScrollPositionChange) {
              scrollSliderComponent.handleScrollPositionChange();
            }
            
            // Double-check buttons are visible after state update
            if (scrollSliderComponent.prevButton && scrollSliderComponent.nextButton) {
              // Only show buttons if there's more than 1 page
              if (scrollSliderComponent.totalPages > 1) {
                scrollSliderComponent.prevButton.hidden = false;
                scrollSliderComponent.nextButton.hidden = false;
              }
            }
          }, 200);
        } else {
          // Fallback: trigger a resize event to activate ResizeObserver
          const resizeEvent = new Event('resize');
          window.dispatchEvent(resizeEvent);
        }
      });
    });

    // Re-bind scroll listener (in case the scroller was recreated)
    if (this.controller) {
      this.controller.abort();
    }
    this.controller = new AbortController();
    this.scroller = this.querySelector(this.selectors.scroller);
    this.indicatorCursor = this.querySelector(this.selectors.indicatorCursor);
    
    if (this.scroller && this.indicatorCursor) {
      this.scroller.addEventListener("scroll", this.#handleScroll.bind(this), {
        signal: this.controller.signal,
      });
    }

    console.log('✅ Slider rebuilt successfully');
  }

  disconnectedCallback() {
    this.controller.abort();
  }
}

if (!customElements.get("scroll-slider-with-scroll-indicator")) {
  customElements.define(
    "scroll-slider-with-scroll-indicator",
    ScrollSliderWithScrollIndicator,
  );
}

/**
 * Global helper function to rebuild an ingredients slider
 * @param {String} newHTML - The new HTML content for slider items
 * @param {Number} newItemCount - The new item count for the scroll indicator
 * @param {HTMLElement} container - Optional container element. If not provided, finds first ingredients slider
 */
window.rebuildIngredientsSlider = function(newHTML, newItemCount, container = null) {
  const sliderContainer = container || document.querySelector('[data-ingredients-slider]');
  
  if (!sliderContainer) {
    console.error('❌ Ingredients slider container not found');
    return;
  }

  const sliderComponent = sliderContainer.querySelector('scroll-slider-with-scroll-indicator');
  
  if (!sliderComponent) {
    console.error('❌ Scroll slider component not found');
    return;
  }

  sliderComponent.rebuildSlider(newHTML, newItemCount);
};
